import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as transportActions from '../../actions/transport';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn, MikaAction } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaExport from '../../components/MikaExport/MikaExport';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import TransportCardGrid from './TransportCardGrid';
import TransportMapView from './TransportMapView';
import { TRANSPORT_STATUS_COLORS, isMobile } from '../../constants/constants';
import { TransportStats, TransportListItem, Pagination } from '../../reducers/transport';
import '../Contact/Contact.scss';

const VIEW_STORAGE_KEY = 'mikasoft_transport_view';

const fmtDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const fmtDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

type StripFilter = 'waiting' | 'inTransit' | 'delivered' | 'withoutTransport' | 'map';
type ViewMode = 'table' | 'card';

interface Props {
    stats: TransportStats | null;
    transports: TransportListItem[];
    transportsPagination: Pagination;
    availableItems: any[];
    availableItemsPagination: Pagination;
    withoutTransportItems: any[];
    withoutTransportPagination: Pagination;
    drivers: Array<{ id: number; name: string }>;
    locations: any[];
    loading: boolean;
    companyProfile: any;
    actions: any;
    history: any;
    location: any;
    match: any;
}

const TransportListPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    // Translate a transport row's status (numeric) to a locale-aware label.
    // Backend-provided `statusLabel` is always Albanian; we map on the client.
    const getStatusLabel = useCallback((row: any) => {
        if (row?.status === 1) return fm('transport.strip.inTransit', 'Në transport');
        if (row?.status === 2) return fm('transport.statusRow.delivered', 'Dërguar');
        return row?.statusLabel || '';
    }, [fm]);

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        strip: { default: 'waiting', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
    });

    const currentPage = filters.page as number;
    const perPage = filters.perPage as number;
    const searchTerm = filters.search as string;
    const stripFilter = (filters.strip || 'waiting') as StripFilter;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeCity = filters.city || null;

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        const saved = localStorage.getItem(VIEW_STORAGE_KEY);
        return (saved === 'card') ? 'card' : 'table';
    });
    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [cityPopoverPos, setCityPopoverPos] = useState({ top: 0, left: 0 });
    const stripRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        props.actions.fetchTransportDashboardStats();
    }, []);

    useEffect(() => { fetchData(); }, [props.location.search]);

    useEffect(() => {
        if (stripFilter === 'map') {
            props.actions.fetchTransportLocations({});
        }
    }, [stripFilter]);

    const fetchData = () => {
        if (stripFilter === 'map') return;
        if (stripFilter === 'waiting') {
            const search = [activeCity, searchTerm].filter(Boolean).join(' ');
            props.actions.fetchAvailableItems(search, currentPage, perPage);
        } else if (stripFilter === 'withoutTransport') {
            props.actions.fetchWithoutTransport({
                page: currentPage, per_page: perPage,
                search: searchTerm || undefined,
            });
        } else {
            const f: any = {
                page: currentPage, per_page: perPage,
                status: stripFilter === 'inTransit' ? 1 : 2,
            };
            if (dateRange?.from) f.start_date = dateRange.from;
            if (dateRange?.to) f.end_date = dateRange.to;
            if (searchTerm) f.search = searchTerm;
            props.actions.fetchTransports(f);
        }
    };

    const dashboardStats = useMemo(() => {
        const s = props.stats;
        if (!s) return [];
        return [
            { key: 'waiting',          label: fm('transport.strip.waiting', 'Në pritje'),           value: s.waiting ?? 0,                                        icon: 'alert' },
            { key: 'inTransit',        label: fm('transport.strip.inTransit', 'Në transport'),      value: s.inTransit ?? 0,                                      icon: 'box' },
            { key: 'delivered',        label: fm('transport.strip.delivered', 'Të dërguara'),       value: s.delivered ?? 0,                                      icon: 'check' },
            { key: 'withoutTransport', label: fm('transport.strip.withoutTransport', 'Pa transport'), value: s.withoutTransportWaiting ?? s.withoutTransport ?? 0, icon: 'folder' },
            { key: 'cities',           label: fm('transport.strip.cities', 'Qytetet'),              value: s.cities?.length ?? 0,                                 icon: 'map' },
            { key: 'map',              label: fm('transport.strip.map', 'Harta'),                   value: '',                                                    icon: 'map' },
        ];
    }, [props.stats, fm]);

    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, left: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        if (cards[cardIndex]) {
            const rect = cards[cardIndex].getBoundingClientRect();
            return { top: rect.bottom + 4, left: rect.left };
        }
        return { top: 0, left: 0 };
    };

    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setCityPopoverPos(positionPopoverUnderCard(4));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        if (key !== 'map') {
            setFilters({ strip: key, city: '', page: 1 });
        } else {
            setFilters({ strip: key, page: 1 });
        }
    };
    const handleViewChange = (mode: 'table' | 'card') => { setViewMode(mode); localStorage.setItem(VIEW_STORAGE_KEY, mode); };
    const handleRowClick = (row: any) => {
        if (stripFilter === 'waiting') {
            if (row.id) props.history.push(`/dashboard/transport/create?wr=${row.id}`);
        } else if (stripFilter === 'withoutTransport') {
            if (row.transport_status === 'completed' && row.transport_id) {
                props.history.push(`/dashboard/transport/${row.transport_id}`);
            } else {
                props.history.push(`/dashboard/transport/create-without?wr=${row.id}`);
            }
        } else {
            if (row.id) props.history.push(`/dashboard/transport/${row.id}`);
        }
    };
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => replaceFilters({ search: e.target.value, page: 1 });
    const clearSearch = () => replaceFilters({ search: '', page: 1 });
    const handleDateChange = (range: DateRange | null) => setFilters({ dateFrom: range?.from || '', dateTo: range?.to || '', page: 1 });

    const effectiveView: 'table' | 'card' = isMobile ? 'table' : viewMode;

    // --- Waiting columns (warehouse records) ---
    const waitingColumns: MikaColumn[] = useMemo(() => [
        { key: 'label',       title: fm('transport.col.label', 'Etiketa'), sortable: true, width: '120px' },
        { key: 'clientName',  title: fm('production.detail.client', 'Klienti'), sortable: true,
            render: (_val: any, row: any) => row.client?.name || '-' },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'),
            render: (_val: any, row: any) => row.project?.name || '-' },
        { key: 'clientCity',    title: fm('transport.col.city', 'Qyteti'),   hideOnMobile: true,
            render: (_val: any, row: any) => row.client?.city || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'clientCountry', title: fm('transport.col.country', 'Shteti'), hideOnMobile: true,
            render: (_val: any, row: any) => row.client?.country || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'products', title: fm('transport.col.products', 'Produkte'), width: '80px',
            render: (val: any) => {
                const count = val?.length || 0;
                return (
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#1e3a5f', background: '#dbeafe' }}>
                        {count}
                    </span>
                );
            }
        },
        { key: 'createdAt', title: fm('transport.col.date', 'Data'), sortable: true, hideOnMobile: true, width: '100px',
            render: (val: string) => fmtDate(val) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    // --- Without transport columns ---
    const withoutTransportColumns: MikaColumn[] = useMemo(() => [
        { key: 'label',       title: fm('transport.col.label', 'Etiketa'), sortable: true, width: '120px' },
        { key: 'clientName',  title: fm('production.detail.client', 'Klienti'), sortable: true,
            render: (_val: any, row: any) => row.client_name || '-' },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'),
            render: (_val: any, row: any) => row.project_name || '-' },
        { key: 'clientCity',    title: fm('transport.col.city', 'Qyteti'),   hideOnMobile: true,
            render: (_val: any, row: any) => row.client_city || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'clientCountry', title: fm('transport.col.country', 'Shteti'), hideOnMobile: true,
            render: (_val: any, row: any) => row.client_country || <span style={{ color: '#d0dbe8' }}>—</span> },
        { key: 'transport_status', title: fm('production.products.column.status', 'Statusi'), width: '120px',
            render: (val: string) => {
                const isCompleted = val === 'completed';
                return (
                    <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                        fontSize: 10, fontWeight: 600,
                        color: isCompleted ? '#16a34a' : '#d97706',
                        background: isCompleted ? '#f0fdf4' : '#fffbeb',
                    }}>
                        {isCompleted ? fm('transport.status.completed', 'E përfunduar') : fm('transport.status.waiting', 'Në pritje')}
                    </span>
                );
            }
        },
        { key: 'created_at', title: fm('transport.col.date', 'Data'), sortable: true, hideOnMobile: true, width: '100px',
            render: (val: string) => fmtDate(val) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    // --- Transport columns ---
    const transportColumns: MikaColumn[] = useMemo(() => [
        { key: 'code', title: fm('transport.col.code', 'Kodi'), sortable: true, width: '100px',
            render: (val: string) => val ? (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#475569' }}>{val.substring(0, 8)}</span>
            ) : '-' },
        { key: 'clientNames',  title: fm('production.detail.client', 'Klienti'), sortable: true },
        { key: 'projectNames', title: fm('production.detail.project', 'Projekti'), hideOnMobile: true },
        { key: 'status', title: fm('production.products.column.status', 'Statusi'), sortable: true, width: '120px',
            render: (val: number, row: TransportListItem) => {
                const colors = TRANSPORT_STATUS_COLORS[val] || { color: '#94a3b8', bg: '#f1f5f9' };
                return (
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: colors.color, background: colors.bg }}>
                        {getStatusLabel(row)}
                    </span>
                );
            }
        },
        { key: 'itemCount', title: fm('transport.col.items', 'Artikuj'), width: '70px',
            render: (val: number) => (
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#1e3a5f', background: '#dbeafe' }}>
                    {val || 0}
                </span>
            )
        },
        { key: 'address', title: fm('transport.col.destination', 'Destinacioni'), hideOnMobile: true,
            render: (val: string) => val ? (val.length > 30 ? val.substring(0, 30) + '...' : val) : '-' },
        { key: 'driver', title: fm('transport.col.driver', 'Shoferi'), sortable: true, hideOnMobile: true,
            render: (val: any) => val?.name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{fm('transport.driver.unassigned', 'Pa caktuar')}</span> },
        { key: 'departureDate', title: fm('transport.col.departure', 'Nisja'), sortable: true, hideOnMobile: true,
            render: (val: string) => val ? (
                <span style={{ fontSize: 11 }}>{fmtDateTime(val)}</span>
            ) : '-' },
        { key: 'arrivalDate', title: fm('transport.col.arrival', 'Arritja'), sortable: true, hideOnMobile: true,
            render: (val: string) => val ? (
                <span style={{ fontSize: 11 }}>{fmtDateTime(val)}</span>
            ) : '-' },
        { key: 'duration', title: fm('transport.col.duration', 'Kohëzgjatja'), hideOnMobile: true, width: '100px',
            render: (val: string) => val || '-' },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    const columns = stripFilter === 'waiting' ? waitingColumns : stripFilter === 'withoutTransport' ? withoutTransportColumns : transportColumns;
    const tableData = stripFilter === 'waiting' ? props.availableItems : stripFilter === 'withoutTransport' ? props.withoutTransportItems : props.transports;
    const tablePagination = stripFilter === 'waiting' ? props.availableItemsPagination : stripFilter === 'withoutTransport' ? props.withoutTransportPagination : props.transportsPagination;

    const tableActions: MikaAction[] = stripFilter === 'waiting' ? [] : [
        {
            icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
            tooltip: fm('transport.action.view', 'Shiko'),
            onClick: handleRowClick,
        },
    ];

    const exportColumns = stripFilter === 'waiting'
        ? [
            { key: 'label',    title: fm('transport.col.label', 'Etiketa') },
            { key: 'client',   title: fm('production.detail.client', 'Klienti'),  render: (row: any) => row.client?.name || '' },
            { key: 'project',  title: fm('production.detail.project', 'Projekti'), render: (row: any) => row.project?.name || '' },
            { key: 'client',   title: fm('transport.col.city', 'Qyteti'),          render: (row: any) => row.client?.city || '' },
            { key: 'client',   title: fm('transport.col.country', 'Shteti'),       render: (row: any) => row.client?.country || '' },
            { key: 'products', title: fm('transport.col.products', 'Produkte'),    render: (row: any) => String(row.products?.length || 0) },
            { key: 'createdAt', title: fm('transport.col.date', 'Data') },
        ]
        : [
            { key: 'code',         title: fm('transport.col.code', 'Kodi'),                 render: (row: any) => row.code?.substring(0, 8) || '' },
            { key: 'clientNames',  title: fm('production.detail.client', 'Klienti') },
            { key: 'projectNames', title: fm('production.detail.project', 'Projekti') },
            { key: 'statusLabel',  title: fm('production.products.column.status', 'Statusi'), render: (row: any) => getStatusLabel(row) },
            { key: 'itemCount',    title: fm('transport.col.items', 'Artikuj'),             render: (row: any) => String(row.itemCount || 0) },
            { key: 'address',      title: fm('transport.col.destination', 'Destinacioni') },
            { key: 'driver',       title: fm('transport.col.driver', 'Shoferi'),            render: (row: any) => row.driver?.name || '' },
            { key: 'departureDate', title: fm('transport.col.departure', 'Nisja') },
            { key: 'arrivalDate',   title: fm('transport.col.arrival', 'Arritja') },
            { key: 'duration',      title: fm('transport.col.duration', 'Kohëzgjatja') },
        ];

    const fetchAllForExport = async () => {
        if (stripFilter === 'waiting') {
            const res = await props.actions.fetchAvailableItems(searchTerm, 1, 0);
            return res?.data || props.availableItems;
        }
        const f: any = { per_page: 0, status: stripFilter === 'inTransit' ? 1 : 2 };
        if (dateRange?.from) f.start_date = dateRange.from;
        if (dateRange?.to) f.end_date = dateRange.to;
        if (searchTerm) f.search = searchTerm;
        const res = await props.actions.fetchTransports(f);
        return res?.data || props.transports;
    };

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={fm('transport.title', 'Transport')}
                subtitle={
                    stripFilter === 'waiting' ? fm('transport.subtitle.waiting', '{count} produkte në pritje', { count: tablePagination.total })
                    : stripFilter === 'inTransit' ? fm('transport.subtitle.inTransit', '{count} në transport', { count: tablePagination.total })
                    : stripFilter === 'withoutTransport' ? fm('transport.subtitle.withoutTransport', '{count} pa transport', { count: tablePagination.total })
                    : fm('transport.subtitle.delivered', '{count} të dërguara', { count: tablePagination.total })
                }
                actions={
                    <button
                        onClick={() => props.history.push('/dashboard/transport/create')}
                        style={{
                            padding: '7px 16px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.12)'; }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        <FormattedMessage id="transport.createButton" defaultMessage="Krijo Transport" />
                    </button>
                }
            />

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input className="clp-search-input" type="text" placeholder={stripFilter === 'waiting' ? fm('transport.search.products', 'Kërko produkte...') : fm('transport.search.transports', 'Kërko transporta...')} value={searchTerm} onChange={handleSearch} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={clearSearch}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={handleDateChange} />
                    {!isMobile && stripFilter !== 'map' && (
                        <MikaViewToggle mode={viewMode} onChange={handleViewChange} />
                    )}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('transport.exportFilename', 'transporta')} title={fm('transport.exportTitle', 'Transporta')} companyProfile={props.companyProfile} />
                </div>
            </div>

            <div ref={stripRef}>
                <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
            </div>

            {/* City popover */}
            {cityPopoverOpen && (
                <div className="clp-city-popover" style={{ top: cityPopoverPos.top, left: cityPopoverPos.left }}>
                    <div className="clp-city-header"><FormattedMessage id="transport.cityPopover.header" defaultMessage="Qytetet" /></div>
                    <button className="clp-city-row" onClick={() => { setFilters({ city: '', page: 1 }); setCityPopoverOpen(false); }}>
                        <span className="clp-city-name"><FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" /></span>
                    </button>
                    {(props.stats?.cities || []).map((c: any) => (
                        <button key={c.city} className={`clp-city-row ${activeCity === c.city ? 'clp-city-row-active' : ''}`}
                            onClick={() => { setFilters({ city: c.city, page: 1 }); setCityPopoverOpen(false); }}>
                            <span className="clp-city-name">{c.city}</span>
                            <span className="clp-city-count">{c.count}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Active city filter chip */}
            {activeCity && (
                <div className="clp-active-filter-bar">
                    <div className="clp-active-filter-chip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span>{activeCity}</span>
                        <button className="clp-active-filter-clear" onClick={() => setFilters({ city: '', page: 1 })}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Map view */}
            {stripFilter === 'map' && (
                <TransportMapView
                    locations={props.locations}
                    onMarkerClick={(item: any) => {
                        if (item.transportId) props.history.push(`/dashboard/transport/${item.transportId}`);
                    }}
                    onCreateTransport={() => props.history.push('/dashboard/transport/create')}
                />
            )}

            {/* Table/Card view */}
            {stripFilter !== 'map' && effectiveView === 'table' && (
                <MikaTable columns={columns} data={tableData} actions={tableActions} loading={props.loading}
                    onRowClick={handleRowClick}
                    emptyMessage={stripFilter === 'waiting' ? fm('transport.empty.waiting', 'Nuk ka produkte në pritje') : stripFilter === 'withoutTransport' ? fm('transport.empty.withoutTransport', 'Nuk ka produkte pa transport') : fm('transport.empty.transports', 'Nuk ka transporta')} />
            )}
            {stripFilter !== 'map' && effectiveView === 'card' && (
                <TransportCardGrid
                    data={tableData}
                    onEdit={handleRowClick}
                    mode={stripFilter === 'waiting' ? 'waiting' : stripFilter === 'withoutTransport' ? 'withoutTransport' : 'transport'}
                />
            )}

            {/* Pagination — hidden for map */}
            {stripFilter !== 'map' && (
                <PaginationComponent currentPage={currentPage} perPage={perPage} pagination={tablePagination}
                    onPageChange={(p: number) => setFilter('page', p)} onPerPageChange={(pp: number) => setFilters({ perPage: pp, page: 1 })}
                    label={stripFilter === 'waiting' ? fm('transport.recordsLabel.products', 'produkte') : fm('transport.recordsLabel.transports', 'transporta')} />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    stats: state.transport.stats,
    transports: state.transport.transports,
    transportsPagination: state.transport.transportsPagination,
    availableItems: state.transport.availableItems,
    availableItemsPagination: state.transport.availableItemsPagination,
    withoutTransportItems: state.transport.withoutTransportItems,
    withoutTransportPagination: state.transport.withoutTransportPagination,
    drivers: state.transport.drivers,
    locations: state.transport.locations,
    loading: state.app.loading,
    companyProfile: state.app.companyProfile,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(transportActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TransportListPage) as any) as any;
